import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Divider, Typography } from "@mui/material";
import type { SxProps, SystemStyleObject } from "@mui/system";
import { useRef } from "react";
import { CommonQuestionErrorComp, Hints } from "../../../components/ui";
import { useThemeContext } from "../../../globalContext/global-context";
import type { ThemeV2 } from "../../../util/types";
import {
  AnswerContent,
  type QuestionGroupHostFrameProps,
  type QuestionHostFrameProps,
} from "../../QuestionFrameShared";
import {
  FormsHostControls,
  FormsPendingOverlay,
  FormsPrimaryAction,
} from "./util/FormsControls";

type QuestionGroupItemStatus = "complete" | "error" | "pending";

export const FormsQuestionGroupItemFrame = (props: QuestionHostFrameProps) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const status = props.prompt.error
    ? "error"
    : props.answered || props.submittable
      ? "complete"
      : "pending";
  const statusLabel =
    status === "complete"
      ? props.answered
        ? theme.copy.status.answered
        : theme.copy.status.readyToSubmit
      : status === "error"
        ? theme.copy.status.needsCorrection
        : theme.copy.status.pending;

  return (
    <Box
      data-testid={`question-group-item-${props.attemptId}`}
      sx={questionGroupItemStyle(Boolean(props.answered))}
    >
      <Box sx={questionGroupPromptRowStyle}>
        {status !== "pending" && (
          <Box
            data-testid={`question-group-status-${status}`}
            role="status"
            aria-label={statusLabel}
            sx={questionGroupStatusStyle(theme, status)}
          >
            {status === "complete" ? (
              <CheckIcon sx={questionGroupStatusIconStyle} />
            ) : (
              <CloseIcon sx={questionGroupStatusIconStyle} />
            )}
          </Box>
        )}
        <Box sx={questionGroupPromptContentStyle}>
          <CommonQuestionErrorComp
            questionText={props.prompt.content}
            image={props.prompt.image}
            alt_text={props.prompt.altText}
            questionSx={questionGroupPromptTextStyle}
          />
        </Box>
      </Box>
      <Box
        aria-disabled={props.answered || undefined}
        inert={props.answered || undefined}
        sx={questionGroupAnswerStyle(
          theme,
          Boolean(props.answered),
          Boolean(props.prompt.error),
        )}
      >
        <AnswerContent
          controls={props.controls}
          primaryActionOwner={props.primaryActionOwner}
          input={props.input}
          hidePrimaryAction
          renderControls={(controls) => (
            <FormsHostControls
              controls={{ ...controls, primaryAction: undefined }}
              input={props.input}
              arrangement="compact"
              autoFocus={false}
              dense={props.arrangement === "compact"}
            />
          )}
          responseContent={props.response}
        />
        {props.prompt.error && (
          <Hints
            text={props.prompt.error}
            variant="error"
            testId="question-group-item-error"
            sx={questionGroupErrorStyle(theme)}
          />
        )}
      </Box>
    </Box>
  );
};

export const FormsQuestionGroupFrame = ({
  heading,
  error,
  children,
  controls,
  containerType = "question_group",
}: QuestionGroupHostFrameProps) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const action = controls.primaryAction;
  const actionRef = useRef<HTMLButtonElement>(null);
  return (
    <Box
      data-testid={
        containerType === "grid_question_group"
          ? "grid-question-group-form"
          : "question-group-form"
      }
      data-question-group-type={containerType}
      aria-busy={action?.pending || undefined}
      sx={questionGroupFrameStyle(theme, containerType)}
    >
      <Typography component="h1" sx={questionGroupHeadingStyle(theme)}>
        {heading}
      </Typography>
      <Divider sx={questionGroupDividerStyle(theme)} />
      {error && (
        <Hints
          text={error}
          variant="error"
          testId="question-group-error"
          sx={questionGroupErrorStyle(theme)}
        />
      )}
      <Box
        data-testid={
          containerType === "grid_question_group"
            ? "grid-question-group-children"
            : "question-group-children"
        }
        inert={action?.pending || undefined}
        aria-disabled={action?.pending || undefined}
        sx={questionGroupChildrenStyle(theme, containerType)}
      >
        {children}
      </Box>
      <Box sx={questionGroupActionsStyle(theme)}>
        {action && (
          <FormsPrimaryAction
            action={action}
            buttonRef={actionRef}
            minWidth={170}
            testId="question-group-submit"
          />
        )}
      </Box>
      <FormsPendingOverlay
        open={Boolean(action?.pending)}
        anchorEl={actionRef.current}
      />
    </Box>
  );
};

const questionGroupItemStyle = (answered: boolean): SystemStyleObject => ({
  display: "flex",
  flexDirection: "column",
  gap: 1.5,
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  cursor: answered ? "not-allowed" : undefined,
  "& [data-select-view]": {
    alignItems: "flex-start",
    boxSizing: "border-box",
    minWidth: 0,
    paddingInline: 0,
  },
  "& [data-testid='choice-group']": {
    alignItems: "flex-start",
    boxSizing: "border-box",
    height: "auto",
    justifyContent: "flex-start",
    minWidth: 0,
  },
  "& [data-testid='rank-sort-view']": {
    maxWidth: "100%",
    minWidth: 0,
  },
  "& [data-testid='rank-sort-view'] > *": {
    boxSizing: "border-box",
    maxWidth: "100%",
    minWidth: 0,
  },
  "& [data-testid='rank-sort-view'] > :first-of-type": {
    marginInline: 0,
    minHeight: 0,
  },
  "& [data-testid='text-list-simultaneous']": {
    maxWidth: "100%",
    minWidth: 0,
  },
  "& [data-testid='text-list-simultaneous'] > *": {
    maxWidth: "100%",
    minWidth: 0,
  },
  "& [data-testid='text-list-simultaneous'] > * > :last-child": {
    boxSizing: "border-box",
    flex: "1 1 0",
    minWidth: 0,
    width: "auto",
  },
});

const questionGroupPromptRowStyle: SxProps = {
  display: "flex",
  alignItems: "flex-start",
  gap: 1,
  cursor: "default",
};

const questionGroupStatusStyle = (
  theme: ThemeV2,
  status: QuestionGroupItemStatus,
): SystemStyleObject => {
  const color =
    status === "error"
      ? theme.tokens.color.feedback.error
      : theme.tokens.color.action.primary;

  return {
    alignItems: "center",
    border: `1px solid ${color}`,
    borderRadius: "50%",
    color,
    display: "flex",
    flex: "0 0 22px",
    height: 22,
    justifyContent: "center",
    marginTop: "1px",
    width: 22,
    marginRight: "1px",
  };
};

const questionGroupStatusIconStyle: SxProps = { fontSize: 16 };
const questionGroupPromptContentStyle: SxProps = { flex: 1, minWidth: 0 };
const questionGroupPromptTextStyle: SxProps = { textAlign: "left" };

const questionGroupAnswerStyle = (
  theme: ThemeV2,
  answered: boolean,
  hasError: boolean,
): SystemStyleObject => ({
  ...(answered ? { pointerEvents: "none" } : {}),
  "& [data-testid='forms-host-input-surface']": {
    borderColor: hasError ? theme.tokens.color.feedback.error : undefined,
    padding: 0,
    marginTop: 1.2,
  },
});

const questionGroupErrorStyle = (theme: ThemeV2): SystemStyleObject => ({
  fontSize: theme.tokens.typography.size.sm,
  mt: `${theme.tokens.spacing["1"]}px`,
  textAlign: "left",
});

const questionGroupFrameStyle = (
  theme: ThemeV2,
  containerType: NonNullable<QuestionGroupHostFrameProps["containerType"]>,
): SystemStyleObject => ({
  width:
    containerType === "grid_question_group"
      ? { xs: "100%", md: "90%" }
      : { xs: "100%", md: "80%" },
  maxWidth: "100%",
  minWidth: 0,
  margin: "0 auto",
  position: "relative",
  boxSizing: "border-box",
  bgcolor: theme.tokens.color.surface.card,
  borderTop: `6px solid ${theme.tokens.color.action.primary}`,
  boxShadow: theme.tokens.elevation["3"],
  padding: {
    xs: `${theme.tokens.spacing["4"]}px`,
    md: `${theme.tokens.spacing["6"]}px`,
  },
});

const questionGroupHeadingStyle = (theme: ThemeV2): SystemStyleObject => ({
  color: theme.tokens.color.text.primary,
  fontSize: theme.tokens.typography.size.xl,
  fontWeight: theme.tokens.typography.weight.bold,
});

const questionGroupDividerStyle = (theme: ThemeV2): SystemStyleObject => ({
  my: `${theme.tokens.spacing["4"]}px`,
});

const questionGroupChildrenStyle = (
  theme: ThemeV2,
  containerType: NonNullable<QuestionGroupHostFrameProps["containerType"]>,
): SystemStyleObject => ({
  display: "flex",
  flexDirection: "column",
  gap:
    containerType === "grid_question_group"
      ? 0
      : `${theme.tokens.spacing["5"]}px`,
});

const questionGroupActionsStyle = (theme: ThemeV2): SystemStyleObject => ({
  display: "flex",
  justifyContent: "flex-end",
  marginTop: `${theme.tokens.spacing["4"]}px`,
});
